import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { WifiHigh, WifiSlash, SignOut, House, UserCircle, ScanSmiley, ChalkboardTeacher, ShieldWarning, Airplane, BowlFood, Sparkle, BookOpen, ChartBar, Gear, Bell, ListChecks, Users, MosqueIcon, PersonSimple, HandHeart, Database, Broom, FirstAid, Shield, Package } from "@phosphor-icons/react";

// Fallback for MosqueIcon if not exported
const MosqueSafe = MosqueIcon || Sparkle;
import api from "@/lib/api";
import { flushQueue } from "@/lib/offlineSync";
import { canAccess, MENUS } from "@/lib/rbac";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, testid: "nav-dashboard", menu: MENUS.DASHBOARD },
  { to: "/santri", label: "Data Santri", icon: UserCircle, testid: "nav-santri", menu: MENUS.SANTRI },
  { to: "/ustaz", label: "Data Ustaz/Ustazah", icon: ChalkboardTeacher, testid: "nav-ustaz", menu: MENUS.USTAZ },
  { to: "/absensi-santri", label: "Absensi Santri", icon: ScanSmiley, testid: "nav-absensi-santri", menu: MENUS.ABSENSI_SANTRI },
  { to: "/absensi-guru", label: "Absensi Ustaz/Ustazah", icon: ChalkboardTeacher, testid: "nav-absensi-guru", menu: MENUS.ABSENSI_GURU },
  { to: "/divisi/keibadahan", label: "Divisi Keibadahan", icon: MosqueSafe, testid: "nav-divisi-keibadahan", menu: MENUS.DIVISI_KEIBADAHAN },
  { to: "/divisi/lugho", label: "Divisi Lugho (Bahasa)", icon: HandHeart, testid: "nav-divisi-lugho", menu: MENUS.DIVISI_LUGHO },
  { to: "/divisi/kedisiplinan", label: "Divisi Kedisiplinan", icon: ShieldWarning, testid: "nav-divisi-kedisiplinan", menu: MENUS.DIVISI_KEDISIPLINAN },
  { to: "/divisi/kebersihan", label: "Divisi Kebersihan", icon: Broom, testid: "nav-divisi-kebersihan", menu: MENUS.DIVISI_KEBERSIHAN },
  { to: "/divisi/keamanan", label: "Divisi Keamanan", icon: Shield, testid: "nav-divisi-keamanan", menu: MENUS.DIVISI_KEAMANAN },
  { to: "/divisi/kesehatan", label: "Divisi Kesehatan", icon: FirstAid, testid: "nav-divisi-kesehatan", menu: MENUS.DIVISI_KESEHATAN },
  { to: "/divisi/humas", label: "Divisi Humas", icon: PersonSimple, testid: "nav-divisi-humas", menu: MENUS.DIVISI_HUMAS },
  { to: "/divisi/logistik", label: "Divisi Logistik", icon: Package, testid: "nav-divisi-logistik", menu: MENUS.DIVISI_LOGISTIK },
  { to: "/tahfiz-halaqah", label: "Divisi Tahfiz (Halaqah)", icon: BookOpen, testid: "nav-divisi-tahfiz", menu: MENUS.DIVISI_TAHFIZ },
  { to: "/pelanggaran", label: "Pelanggaran (Semua)", icon: ShieldWarning, testid: "nav-pelanggaran", menu: MENUS.PELANGGARAN },
  { to: "/perizinan", label: "Perizinan & Penjemputan", icon: Airplane, testid: "nav-perizinan", menu: MENUS.PERIZINAN },
  { to: "/makan", label: "Absensi Makan", icon: BowlFood, testid: "nav-makan", menu: MENUS.MAKAN },
  { to: "/kegiatan", label: "Absensi Kegiatan", icon: Sparkle, testid: "nav-kegiatan", menu: MENUS.KEGIATAN },
  { to: "/tahfiz", label: "Tahfiz (Setoran)", icon: BookOpen, testid: "nav-tahfiz", menu: MENUS.DIVISI_TAHFIZ },
  { to: "/tahfiz-halaqah", label: "Absensi Halaqah", icon: BookOpen, testid: "nav-tahfiz-halaqah", menu: MENUS.DIVISI_TAHFIZ },
  { to: "/laporan", label: "Laporan", icon: ChartBar, testid: "nav-laporan", menu: MENUS.LAPORAN },
  { to: "/pengaturan", label: "Pengaturan", icon: Gear, testid: "nav-pengaturan", menu: MENUS.PENGATURAN },
  { to: "/audit", label: "Audit Log", icon: ListChecks, testid: "nav-audit", menu: MENUS.AUDIT },
  { to: "/backup", label: "Backup & Restore", icon: Database, testid: "nav-backup", menu: MENUS.BACKUP },
  { to: "/users", label: "Pengguna", icon: Users, testid: "nav-users", menu: MENUS.USERS },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [online, setOnline] = useState(navigator.onLine);
  const [drawer, setDrawer] = useState(false);
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    const on = () => {
      setOnline(true);
      flushQueue(api).then((r) => r?.count && console.log(`[SYNC] ${r.count} items flushed`));
    };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifikasi");
        if (mounted) setUnread(data.unread || 0);
      } catch (_) {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  // Filter NAV based on user role
  const visibleNav = user ? NAV.filter((n) => canAccess(user.role, n.menu)) : [];

  return (
    <div className="min-h-screen flex bg-[#f7f9f7]">
      {/* Sidebar - desktop */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-72 bg-[#14532d] text-green-50 transition-transform duration-300 ${
          drawer ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-6 border-b border-green-800/60">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-white/10 grid place-items-center font-display text-lg font-bold">
              IM
            </div>
            <div className="leading-tight">
              <div className="font-display text-xl font-bold tracking-tight">IMSYAF</div>
              <div className="text-[11px] uppercase tracking-widest text-green-200/80">PP Al-Imam Asy-Syafi'i</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs">
            <span
              data-testid="offline-indicator"
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${
                online ? "bg-green-500/20 text-green-100" : "bg-amber-500/20 text-amber-100"
              }`}
            >
              {online ? <WifiHigh weight="bold" size={14} /> : <WifiSlash weight="bold" size={14} />}
              {online ? "Online" : "Offline"}
            </span>
          </div>
        </div>

        <nav className="p-3 overflow-y-auto h-[calc(100vh-200px)]">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              onClick={() => setDrawer(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-white text-[#14532d]"
                    : "text-green-50/85 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <n.icon size={18} weight="duotone" />
              <span className="truncate">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-green-800/60">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-green-200/80 truncate">{user?.role_label}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              title="Keluar"
              className="p-2 rounded-md hover:bg-white/10 transition-colors"
            >
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile drawer */}
      {drawer && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setDrawer(false)} />}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-slate-200/70">
          <div className="flex items-center justify-between px-5 py-3">
            <button
              className="lg:hidden imsyaf-btn-outline"
              onClick={() => setDrawer(true)}
              data-testid="drawer-open-btn"
              aria-label="Menu"
            >
              ☰
            </button>
            <div className="hidden lg:block">
              <div className="text-xs uppercase tracking-widest text-slate-400">Sistem Manajemen Pesantren</div>
              <div className="font-display text-lg font-semibold text-[#14532d]">Selamat datang, {user?.name?.split(" ")[0] || "User"}</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="notif-btn"
                onClick={() => nav("/notifikasi")}
                className="relative imsyaf-btn-ghost"
                title="Notifikasi"
              >
                <Bell size={18} weight="duotone" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        <main className="p-5 lg:p-8 flex-1">{children}</main>
      </div>
    </div>
  );
}
